import { t } from '../i18n';
//admin-client/src/components/AgentTreeNode.jsx
// admin-client/src/components/AgentTreeNode.jsx
import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import api from '../api/axios';
import AgentAssetControl from './AgentAssetControl';
import EditFeeModal from './EditFeeModal';
import FeeGauge from './FeeGauge';
/* =========================================
 * 공통 훅/쉘: ESC 닫힘 + 바깥 클릭 닫힘
 * =======================================*/
function useCloseOnEsc(handler, enabled = true) {
    const handlerRef = React.useRef(handler);
    React.useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);
    React.useEffect(() => {
        if (!enabled) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') handlerRef.current?.();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [enabled]);
}
function ModalShell({ open, onClose, children, className = 'w-[420px] max-w-[calc(100vw-32px)]' }) {
    useCloseOnEsc(onClose, open);
    if (!open)
        return null;
    return createPortal((<div className="admin-user-modal-portal fixed inset-0 z-30 flex items-start sm:items-center justify-center overflow-y-auto p-4 sm:p-6" role="presentation">
      <div className="admin-modal-overlay fixed inset-0 bg-black/30" onClick={onClose}/>
      <div className={`admin-modal-panel relative w-full max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-4 sm:p-5 shadow-xl ${className}`}>
        {children}
      </div>
    </div>), document.body);
}
ModalShell.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.node,
    className: PropTypes.string,
};
/* =========================================
 * 드롭다운 메뉴: 바깥 클릭+ESC로 닫힘
 * =======================================*/
const Menu = React.forwardRef(function Menu({ open, anchorRef, onClose, children, className = '' }, menuRef) {
    useCloseOnEsc(onClose, open);
    React.useEffect(() => {
        function handleDocMouseDown(e) {
            if (!open)
                return;
            const a = anchorRef?.current || null;
            const m = menuRef?.current || null;
            const target = e.target;
            if ((m && m.contains(target)) || (a && a.contains(target)))
                return;
            onClose?.();
        }
        if (open)
            document.addEventListener('mousedown', handleDocMouseDown, true);
        return () => document.removeEventListener('mousedown', handleDocMouseDown, true);
    }, [open, onClose, anchorRef, menuRef]);
    if (!open)
        return null;
    return (<div ref={menuRef} className={`fixed inset-x-3 bottom-3 z-[60] rounded-xl border border-slate-200 bg-white shadow-2xl sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-full sm:mt-2 sm:w-40 sm:shadow-lg ${className}`}>
      <div className="py-1">{children}</div>
    </div>);
});
Menu.propTypes = {
    open: PropTypes.bool,
    anchorRef: PropTypes.shape({ current: PropTypes.any }),
    onClose: PropTypes.func,
    children: PropTypes.node,
    className: PropTypes.string,
};
/* =========================================
 * 회원 정보 수정 모달(닉네임/비번)
 * =======================================*/
function InfoEditModal({ open, targetId, defaultNickname = '', onApply, onClose }) {
    const [nick, setNick] = React.useState(defaultNickname || '');
    const [pw, setPw] = React.useState('');
    const [pw2, setPw2] = React.useState('');
    React.useEffect(() => {
        setNick(defaultNickname || '');
        setPw('');
        setPw2('');
    }, [open, defaultNickname]);
    return (<ModalShell open={open} onClose={onClose} className="w-[420px] max-w-[calc(100vw-32px)]">
      <div className="text-base font-semibold mb-2 text-center">{t("\uD68C\uC6D0 \uC815\uBCF4 \uC218\uC815")}</div>
      <div className="text-xs text-gray-500 mb-4 text-center">
        {targetId}{t("\uACC4\uC815\uC758 \uC815\uBCF4\uB97C \uC218\uC815\uD569\uB2C8\uB2E4.")}</div>

      <div className="grid grid-cols-1 gap-2">
        <input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder={t("\uB2C9\uB124\uC784")} value={nick} onChange={(e) => setNick(e.target.value)}/>
        <input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder={t("\uBE44\uBC00\uBC88\uD638(\uBBF8\uC785\uB825 \uC2DC \uBCC0\uACBD \uC548 \uD568)")} type="password" value={pw} onChange={(e) => setPw(e.target.value)}/>
        <input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder={t("\uC138\uCEE8\uB4DC \uBE44\uBC00\uBC88\uD638(\uC635\uC158)")} type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}/>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button className="px-3 py-2 rounded-lg border" onClick={onClose}>{t("\uCDE8\uC18C")}</button>
        <button className="px-3 py-2 rounded-lg bg-gray-900 text-white" onClick={() => onApply({
            nickname: nick,
            password: pw || undefined,
            second_password: pw2 || undefined,
        })}>{t("\uC801\uC6A9")}</button>
      </div>
    </ModalShell>);
}
InfoEditModal.propTypes = {
    open: PropTypes.bool,
    targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    defaultNickname: PropTypes.string,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
/* =========================================
 * (간단 폴백) 하위 생성 폼
 * =======================================*/
function CreateChildFallback({ parentAgentId, onClose, onSubmit }) {
    const [form, setForm] = React.useState({
        id: '',
        password: '',
        nickname: '',
        as_agent: true,
        fee_percent: '',
    });
    return (<ModalShell open onClose={onClose} className="admin-account-modal">
      <div className="admin-account-modal-head">
        <div>
          <div className="text-lg font-semibold">{t("\uD558\uC704 \uACC4\uC815 \uC0DD\uC131")}</div>
          <p>{t("\uC0C1\uC704 \uC5D0\uC774\uC804\uD2B8:")} {parentAgentId}</p>
        </div>
        <button type="button" className="h-9 w-9 rounded-lg border text-lg" onClick={onClose} aria-label={t("\uB2EB\uAE30")}>×</button>
      </div>

      <div className="admin-account-modal-body">
        <div className="admin-account-modal-notice">
          <span aria-hidden="true">✓</span>
          <span>{t('하위 에이전트 또는 일반 유저 계정을 생성합니다. 입력한 계정 정보는 생성 즉시 적용됩니다.')}</span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder="id" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })}/>
        <input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder="nickname" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })}/>
        <input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}/>
        <label className="flex items-center gap-2 text-sm py-1">
          <input type="checkbox" checked={form.as_agent} onChange={(e) => setForm({ ...form, as_agent: e.target.checked })}/>{t("\uC5D0\uC774\uC804\uD2B8\uB85C \uC0DD\uC131")}</label>

        {!form.as_agent && (<input className="block w-full min-w-0 box-border border rounded-lg px-3 py-2" placeholder={t("fee_percent(\uC720\uC800)")} type="number" step="0.01" value={form.fee_percent} onChange={(e) => setForm({ ...form, fee_percent: e.target.value })}/>)}
      </div>

        <div className="admin-account-modal-actions sm:col-span-2">
          <button className="px-4 py-2 rounded-lg border" onClick={onClose}>{t("\uCDE8\uC18C")}</button>
          <button className="px-4 py-2 rounded-lg bg-gray-900 text-white" onClick={() => {
              const payload = { ...form };
              // 비어 있으면 서버의 에이전트 수수료 기본값을 적용한다.
              // 일반 유저에서 0을 직접 입력한 경우에만 명시적 0%로 전달한다.
              if (form.as_agent || form.fee_percent === '' || form.fee_percent == null) {
                  delete payload.fee_percent;
              }
              else {
                  payload.fee_percent = Number(form.fee_percent);
              }
              onSubmit(payload);
          }}>{t("\uC0DD\uC131")}</button>
        </div>
      </div>
    </ModalShell>);
}
CreateChildFallback.propTypes = {
    parentAgentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

/* =========================================
 * 테이블 미션 agent 추가 상금 수혜자 정책 모달
 * =======================================*/
function normalizePolicySeed(policy) {
    const p = policy && typeof policy === 'object' ? policy : {};
    const mode = String(p.mode || 'SELF').toUpperCase();
    return {
        mode: ['SELF', 'PARENT', 'ROOT', 'SELECTED', 'SPLIT', 'DISABLED'].includes(mode) ? mode : 'SELF',
        receiver_agent_id: p.receiver_agent_id || p.receiverAgentId || p.agent_id || p.agentId || p.receivers?.[0]?.agent_id || '',
        receivers: Array.isArray(p.receivers) ? p.receivers : [],
    };
}
function splitTextFromReceivers(receivers) {
    return (Array.isArray(receivers) ? receivers : [])
        .map((r) => `${r.agent_id || r.agentId || ''}:${r.percent ?? r.ratio ?? r.share ?? ''}`)
        .filter((line) => line.replace(':', '').trim())
        .join('\n');
}
function parseSplitText(text) {
    const rows = String(text || '').split(/\n+/).map((line) => line.trim()).filter(Boolean);
    return rows.map((line) => {
        const [agentId, percent] = line.split(':').map((v) => String(v || '').trim());
        return { agent_id: agentId, percent: Number(percent) };
    }).filter((r) => r.agent_id && Number.isFinite(r.percent) && r.percent > 0);
}
function MissionBeneficiaryModal({ open, agentId, initialPolicy, onClose, onSaved }) {
    const seed = React.useMemo(() => normalizePolicySeed(initialPolicy), [initialPolicy]);
    const [mode, setMode] = React.useState(seed.mode);
    const [receiverAgentId, setReceiverAgentId] = React.useState(seed.receiver_agent_id || '');
    const [splitText, setSplitText] = React.useState(splitTextFromReceivers(seed.receivers));
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    React.useEffect(() => {
        if (!open)
            return;
        let alive = true;
        setMode(seed.mode);
        setReceiverAgentId(seed.receiver_agent_id || '');
        setSplitText(splitTextFromReceivers(seed.receivers));
        setLoading(true);
        api.get(`/admin/mission/beneficiary/${encodeURIComponent(String(agentId))}`)
            .then(({ data }) => {
            if (!alive)
                return;
            const loaded = normalizePolicySeed(data?.policy);
            setMode(loaded.mode);
            setReceiverAgentId(loaded.receiver_agent_id || '');
            setSplitText(splitTextFromReceivers(loaded.receivers));
        })
            .catch(() => null)
            .finally(() => { if (alive)
            setLoading(false); });
        return () => { alive = false; };
    }, [open, agentId, seed.mode, seed.receiver_agent_id, seed.receivers]);
    const save = async () => {
        try {
            setSaving(true);
            let policy = { mode };
            if (mode === 'SELECTED') {
                if (!receiverAgentId) {
                    alert(t('수혜자 agent_id를 입력하세요.'));
                    return;
                }
                policy = { mode, receiver_agent_id: String(receiverAgentId).trim(), receivers: [{ agent_id: String(receiverAgentId).trim(), percent: 1 }] };
            }
            if (mode === 'SPLIT') {
                const receivers = parseSplitText(splitText);
                const sum = receivers.reduce((acc, r) => acc + Number(r.percent || 0), 0);
                if (!receivers.length || (Math.abs(sum - 1) > 0.0001 && Math.abs(sum - 100) > 0.0001)) {
                    alert(t('분배식은 agent_id:percent 형식이며 percent 합계는 1 또는 100이어야 합니다. 예: 1002:0.7 / 1001:0.3'));
                    return;
                }
                policy = { mode, receivers: receivers.map((r) => ({ ...r, percent: sum > 1 ? r.percent / 100 : r.percent })) };
            }
            const { data } = await api.put(`/admin/mission/beneficiary/${encodeURIComponent(String(agentId))}`, { policy });
            onSaved?.(data?.policy || policy);
            onClose?.();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message || t('수혜자 설정 저장 실패'));
        }
        finally {
            setSaving(false);
        }
    };
    return (<ModalShell open={open} onClose={onClose} className="w-[560px] max-w-[calc(100vw-32px)]">
      <div className="text-base font-semibold mb-1 text-center">{t('미션 상금 수혜자 설정')}</div>
      <div className="text-xs text-gray-500 mb-4 text-center">agent_id: {agentId}</div>

      <div className="space-y-3">
        <label className="block text-sm">
          <span className="block mb-1 text-gray-600">{t('수혜 방식')}</span>
          <select className="block w-full border rounded-lg px-3 py-2" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="SELF">SELF - {t('직속 에이전트가 전부 수령')}</option>
            <option value="PARENT">PARENT - {t('상위 에이전트가 전부 수령')}</option>
            <option value="ROOT">ROOT - {t('최상위 에이전트가 전부 수령')}</option>
            <option value="SELECTED">SELECTED - {t('특정 에이전트가 전부 수령')}</option>
            <option value="SPLIT">SPLIT - {t('여러 에이전트 분배 수령')}</option>
            <option value="DISABLED">DISABLED - {t('에이전트 추가 상금 미지급')}</option>
          </select>
        </label>

        {mode === 'SELECTED' && (<label className="block text-sm">
          <span className="block mb-1 text-gray-600">{t('수혜자 agent_id')}</span>
          <input className="block w-full border rounded-lg px-3 py-2" value={receiverAgentId} onChange={(e) => setReceiverAgentId(e.target.value)} placeholder="1002"/>
        </label>)}

        {mode === 'SPLIT' && (<label className="block text-sm">
          <span className="block mb-1 text-gray-600">{t('분배식')}</span>
          <textarea className="block w-full border rounded-lg px-3 py-2 min-h-[110px] font-mono text-xs" value={splitText} onChange={(e) => setSplitText(e.target.value)} placeholder={'1002:0.7\n1001:0.3'}/>
          <span className="block mt-1 text-[11px] text-gray-500">{t('한 줄에 agent_id:percent 입력. percent 합계는 1 또는 100이어야 합니다.')}</span>
        </label>)}

        <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
          {t('이 설정은 유저 테이블 상금은 건드리지 않고, agent_share로 계산된 추가 상금의 수혜자만 결정합니다.')}
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button className="px-3 py-2 rounded-lg border" onClick={onClose} disabled={saving}>{t('취소')}</button>
        <button className="px-3 py-2 rounded-lg bg-gray-900 text-white disabled:opacity-50" onClick={save} disabled={loading || saving}>{saving ? t('저장 중...') : t('저장')}</button>
      </div>
    </ModalShell>);
}
MissionBeneficiaryModal.propTypes = {
    open: PropTypes.bool,
    agentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    initialPolicy: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
};
/* =========================================
 * 유저 테이블 행
 * =======================================*/
function UserTableRow({ u, onBlock, onDelete, onCredit, onDebit, canDelete = true }) {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [infoOpen, setInfoOpen] = React.useState(false);
    const [mobileInfoOpen, setMobileInfoOpen] = React.useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const menuBtnRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const mobileMenuBtnRef = React.useRef(null);
    const mobileMenuRef = React.useRef(null);
    const mobileAssetActionsRef = React.useRef(null);
    useCloseOnEsc(() => setMobileMenuOpen(false), mobileMenuOpen);
    React.useEffect(() => {
        if (!mobileMenuOpen) return undefined;
        // Keep iOS / Android from scrolling the underlying agent tree while the sheet is open.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        mobileMenuRef.current?.focus();
        const closeOnDesktop = () => {
            if (window.innerWidth > 760) setMobileMenuOpen(false);
        };
        window.addEventListener('resize', closeOnDesktop);
        return () => {
            window.removeEventListener('resize', closeOnDesktop);
            document.body.style.overflow = previousOverflow;
        };
    }, [mobileMenuOpen]);
    const displayName = u.nickname || u.user_code;
    const assetValue = Number(u.asset ?? 0);
    const assetLabel = Number.isFinite(assetValue)
        ? assetValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : '-';
    const saveUserInfo = async (payload) => {
        try {
            await api.post('/admin/users/update', { target_id: u.user_code, ...payload });
            setInfoOpen(false);
            setMobileInfoOpen(false);
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message || t("\uC815\uBCF4 \uC218\uC815 \uC2E4\uD328"));
        }
    };
    return (<tr className="admin-user-mobile-row border-b border-slate-200 odd:bg-white even:bg-slate-50/60 hover:bg-indigo-50/40">
      <td data-label={t('닉네임')} className="px-4 py-3.5 align-middle w-[180px]">
        <div className="text-sm font-semibold text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis">
          {u.nickname || u.user_code}
        </div>
      </td>

      <td data-label={t('아이디')} className="px-4 py-3.5 align-middle w-[180px]">
        <div className="text-sm text-slate-600 whitespace-nowrap overflow-hidden text-ellipsis">
          {u.id || '-'}
        </div>
      </td>

      <td data-label={t('유저코드')} className="px-4 py-3.5 align-middle w-[180px]">
        <div className="text-sm text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis">
          {u.user_code}
        </div>
      </td>

      <td data-label={t('자산')} className="px-4 py-3.5 align-middle min-w-[240px]">
        <div className="flex items-center">
          <AgentAssetControl className="shrink-0" targetId={u.user_code} seedAsset={Number(u.asset ?? 0)} onCredit={onCredit} onDebit={onDebit} label={t("\uC790\uC0B0")}/>
        </div>
      </td>

      <td data-label={t('수수료')} className="px-4 py-3.5 align-middle w-[110px] text-sm font-medium text-slate-700 whitespace-nowrap">
        {Number(u.fee_percent) || 0}%
      </td>

      <td data-label={t('상태')} className="px-4 py-3.5 align-middle w-[110px] whitespace-nowrap">
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border ${u.is_banned
            ? 'bg-red-50 text-red-600 border-red-200'
            : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>
          {u.is_banned ? t("\uCC28\uB2E8\uB428") : t("\uC815\uC0C1")}
        </span>
      </td>

      <td data-label={t('관리')} className="px-4 py-3.5 align-middle w-[190px] text-right whitespace-nowrap">
        <div className="inline-flex items-center justify-end gap-2">
          {canDelete && (<button
            type="button"
            className="h-9 min-w-[68px] px-3 rounded-lg border border-red-200 bg-red-50 text-sm font-semibold text-red-600 hover:bg-red-100"
            onClick={() => onDelete(u.user_code, { type: 'user', label: u.nickname || u.id || u.user_code })}
          >{t("\uC0AD\uC81C")}</button>)}

          <div className="relative inline-flex">
          <button ref={menuBtnRef} className="h-9 min-w-[68px] px-3 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800" onClick={() => setMenuOpen((v) => !v)}>{t("\uC218\uC815")}</button>

          <Menu open={menuOpen} anchorRef={menuBtnRef} onClose={() => setMenuOpen(false)} ref={menuRef}>
            <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={() => {
            setMenuOpen(false);
            setInfoOpen(true);
        }}>{t("\uC815\uBCF4\uC218\uC815")}</button>

            <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={() => {
            setMenuOpen(false);
            onBlock(u.user_code, !(u.is_banned ?? false));
        }}>
              {u.is_banned ? t("\uCC28\uB2E8 \uD574\uC81C") : t("\uCC28\uB2E8")}
            </button>

          </Menu>
          </div>
        </div>

        <InfoEditModal open={infoOpen} targetId={u.user_code} defaultNickname={u.nickname} onClose={() => setInfoOpen(false)} onApply={saveUserInfo}/>
      </td>

      {/* 모바일에서만 1명당 정확히 한 줄. PC의 7개 기존 셀은 그대로 유지. */}
      <td className="admin-user-compact-cell" colSpan={7}>
        <div className="admin-user-compact-line">
          <span className="admin-user-compact-nick" title={`${t('닉네임')}: ${displayName}`}>{displayName}</span>
          <span className="admin-user-compact-id" title={`${t('아이디')}: ${u.id || '-'}`}>{u.id || '-'}</span>
          <span className="admin-user-compact-code" title={`${t('유저코드')}: ${u.user_code}`}>{u.user_code}</span>
          <span className="admin-user-compact-asset" title={`${t('자산')}: ${assetLabel}`}>{assetLabel}</span>
          <span className="admin-user-compact-fee" title={`${t('수수료')}: ${Number(u.fee_percent) || 0}%`}>{Number(u.fee_percent) || 0}%</span>
          <span className={`admin-user-compact-status ${u.is_banned ? 'is-banned' : ''}`} title={`${t('상태')}: ${u.is_banned ? t('차단됨') : t('정상')}`}>
            {u.is_banned ? t('차단됨') : t('정상')}
          </span>
          <button
            type="button"
            ref={mobileMenuBtnRef}
            className="admin-user-compact-more"
            aria-label={`${displayName} ${t('관리')}`}
            aria-expanded={mobileMenuOpen}
            aria-haspopup="dialog"
            onClick={() => setMobileMenuOpen(value => !value)}
          >⋯</button>
        </div>
        {/* 모바일 자산 모달은 항상 마운트: 더보기 닫을 때 지급/차감 모달이 사라지지 않음. */}
        <AgentAssetControl
          ref={mobileAssetActionsRef}
          className="admin-user-compact-wallet-control"
          targetId={u.user_code}
          seedAsset={Number(u.asset ?? 0)}
          onCredit={onCredit}
          onDebit={onDebit}
          label={t('자산')}
        />
        {mobileMenuOpen && createPortal((<div className="admin-user-mobile-layer">
          <button
            type="button"
            className="admin-user-mobile-backdrop"
            aria-label={t('닫기')}
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className="admin-user-mobile-actions-menu"
            ref={mobileMenuRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${displayName} ${t('관리')}`}
            tabIndex={-1}
          >
          <div className="admin-user-mobile-menu-head">
            <strong>{displayName}</strong>
            <button type="button" aria-label={t('닫기')} onClick={() => setMobileMenuOpen(false)}>×</button>
          </div>
          <div className="admin-user-mobile-menu-info">
            <span>{t('아이디')}: {u.id || '-'}</span>
            <span>{t('유저코드')}: {u.user_code}</span>
            <span>{t('자산')}: {assetLabel}</span>
            <span>{t('수수료')}: {Number(u.fee_percent) || 0}%</span>
            <span>{t('상태')}: {u.is_banned ? t('차단됨') : t('정상')}</span>
          </div>
          <div className="admin-user-mobile-menu-actions">
            <button type="button" onClick={() => {
                setMobileMenuOpen(false);
                mobileAssetActionsRef.current?.openCredit();
            }}>{t('지급')}</button>
            <button type="button" onClick={() => {
                setMobileMenuOpen(false);
                mobileAssetActionsRef.current?.openDebit();
            }}>{t('차감')}</button>
            <button type="button" onClick={() => {
                setMobileMenuOpen(false);
                setMobileInfoOpen(true);
            }}>{t('정보 수정')}</button>
            <button type="button" onClick={() => {
                setMobileMenuOpen(false);
                onBlock(u.user_code, !(u.is_banned ?? false));
            }}>{u.is_banned ? t('차단 해제') : t('차단')}</button>
            {canDelete && <button type="button" className="is-danger" onClick={() => {
                setMobileMenuOpen(false);
                onDelete(u.user_code, { type: 'user', label: u.nickname || u.id || u.user_code });
            }}>{t('삭제')}</button>}
          </div>
          </div>
        </div>), document.body)}
        <InfoEditModal open={mobileInfoOpen} targetId={u.user_code} defaultNickname={u.nickname} onClose={() => setMobileInfoOpen(false)} onApply={saveUserInfo}/>
      </td>
    </tr>);
}
UserTableRow.propTypes = {
    u: PropTypes.shape({
        user_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        nickname: PropTypes.string,
        is_banned: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
        fee_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        asset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    onBlock: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCredit: PropTypes.func,
    onDebit: PropTypes.func,
    canDelete: PropTypes.bool,
};

const USER_TABLE_PAGE_SIZE = 20;

function buildUserPageItems(currentPage, totalPages) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    const items = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) items.push('left-ellipsis');
    for (let page = start; page <= end; page += 1) items.push(page);
    if (end < totalPages - 1) items.push('right-ellipsis');
    items.push(totalPages);
    return items;
}

function UserTableList({ users, onBlock, onDelete, onCredit, onDebit, canDelete = true }) {
    const safeUsers = Array.isArray(users) ? users : [];
    const [query, setQuery] = React.useState('');
    const [page, setPage] = React.useState(1);

    const filteredUsers = React.useMemo(() => {
        const needle = String(query || '').trim().toLowerCase();
        if (!needle) return safeUsers;
        return safeUsers.filter((user) => [user.nickname, user.id, user.user_code]
            .some((value) => String(value ?? '').toLowerCase().includes(needle)));
    }, [safeUsers, query]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USER_TABLE_PAGE_SIZE));
    React.useEffect(() => {
        setPage(1);
    }, [query, safeUsers.length]);
    React.useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const pageUsers = React.useMemo(() => {
        const start = (page - 1) * USER_TABLE_PAGE_SIZE;
        return filteredUsers.slice(start, start + USER_TABLE_PAGE_SIZE);
    }, [filteredUsers, page]);
    const pageItems = React.useMemo(() => buildUserPageItems(page, totalPages), [page, totalPages]);

    return (<div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50/70 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('유저 검색')}
            aria-label={t('유저 검색')}
            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 pr-9 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
          {query && (<button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            onClick={() => setQuery('')}
            aria-label={t('초기화')}
          >×</button>)}
        </div>
        <div className="text-xs text-slate-500 whitespace-nowrap">
          {t('총')} {filteredUsers.length}{t('명')} / {safeUsers.length}{t('명')}
        </div>
      </div>

      <div className="admin-user-compact-columns" aria-hidden="true">
        <span>{t('닉네임')}</span><span>{t('아이디')}</span><span>{t('유저코드')}</span>
        <span>{t('자산')}</span><span>{t('수수료')}</span><span>{t('상태')}</span><span></span>
      </div>
      <div className="overflow-x-auto">
        <table className="admin-user-table w-full min-w-[1120px] border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap w-[180px]">{t('닉네임')}</th>
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap w-[180px]">{t('아이디')}</th>
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap w-[180px]">{t('유저코드')}</th>
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap min-w-[240px]">{t('자산')}</th>
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap w-[110px]">{t('수수료')}</th>
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap w-[110px]">{t('상태')}</th>
              <th className="px-4 py-3.5 text-xs font-semibold text-slate-500 whitespace-nowrap text-right w-[190px]">{t('관리')}</th>
            </tr>
          </thead>
          <tbody>
            {pageUsers.length > 0 ? pageUsers.map((user) => (<UserTableRow
              key={user.user_code}
              u={user}
              onBlock={onBlock}
              onDelete={onDelete}
              onCredit={onCredit}
              onDebit={onDebit}
              canDelete={canDelete}
            />)) : (<tr>
              <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">{t('검색 결과가 없습니다.')}</td>
            </tr>)}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (<div className="flex flex-col gap-2 border-t border-slate-200 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-slate-500">{t('페이지')} {page} / {totalPages}</div>
        <div className="flex flex-wrap items-center justify-end gap-1">
          <button type="button" className="h-9 rounded-lg border border-slate-200 px-3 text-sm disabled:cursor-not-allowed disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>{t('이전')}</button>
          {pageItems.map((item) => typeof item === 'number' ? (<button
            type="button"
            key={item}
            className={`h-9 min-w-9 rounded-lg border px-2 text-sm ${item === page ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
            onClick={() => setPage(item)}
          >{item}</button>) : (<span key={item} className="px-1 text-slate-400">…</span>))}
          <button type="button" className="h-9 rounded-lg border border-slate-200 px-3 text-sm disabled:cursor-not-allowed disabled:opacity-40" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>{t('다음')}</button>
        </div>
      </div>)}
    </div>);
}
UserTableList.propTypes = {
    users: PropTypes.arrayOf(PropTypes.shape({
        user_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        nickname: PropTypes.string,
        is_banned: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
        fee_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        asset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })).isRequired,
    onBlock: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCredit: PropTypes.func,
    onDebit: PropTypes.func,
    canDelete: PropTypes.bool,
};
/* =========================================
 * 메인 트리 노드
 * =======================================*/
export default function AgentTreeNode({ node, onCreate, onBlock, onDelete, onUpdateFee, onCredit, onDebit, isRoot = false, viewerIsSuperadmin = false, ancestorFeeTotal = 0, depth = 1, }) {
    const [openCreateModal, setOpenCreateModal] = React.useState(false);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [feeOpen, setFeeOpen] = React.useState(false);
    const [infoOpen, setInfoOpen] = React.useState(false);
    const [playerFeeOpen, setPlayerFeeOpen] = React.useState(false);
    const [beneficiaryOpen, setBeneficiaryOpen] = React.useState(false);
    const [localMissionRewardPolicy, setLocalMissionRewardPolicy] = React.useState(node.mission_reward_policy || { mode: 'SELF', receivers: [] });
    const collapseDepth = Math.min(Math.max(Number(depth) || 1, 1), 3);
    const collapseStorageKey = `npc-admin-agent-collapse:${node.agent_id}`;
    const [expanded, setExpanded] = React.useState(() => {
        try {
            const saved = localStorage.getItem(collapseStorageKey);
            return saved == null ? !!isRoot : saved === '1';
        }
        catch {
            return !!isRoot;
        }
    });
    const menuBtnRef = React.useRef(null);
    const menuRef = React.useRef(null);
    useCloseOnEsc(() => {
        setMenuOpen(false);
        setFeeOpen(false);
        setInfoOpen(false);
        setPlayerFeeOpen(false);
        setBeneficiaryOpen(false);
    }, menuOpen || feeOpen || infoOpen || playerFeeOpen || beneficiaryOpen);
    React.useEffect(() => {
        setLocalMissionRewardPolicy(node.mission_reward_policy || { mode: 'SELF', receivers: [] });
    }, [node.mission_reward_policy]);
    React.useEffect(() => {
        try {
            localStorage.setItem(collapseStorageKey, expanded ? '1' : '0');
        }
        catch {
            // 저장소 사용 불가 환경에서는 펼침 상태만 현재 세션에 유지
        }
    }, [collapseStorageKey, expanded]);
    const counts = React.useMemo(() => {
        const agentCnt = (node.children || []).length;
        const userCnt = (node.users || []).length;
        return { agentCnt, userCnt };
    }, [node.children, node.users]);
    const isLv4 = Number(node.level) === 4;
    const hasUsers = Array.isArray(node.users) && node.users.length > 0;
    const canDeleteAgent = !isRoot && (viewerIsSuperadmin || Number(depth) === 2);
    const canDeleteUsersHere = viewerIsSuperadmin || isRoot;
    // fee_percent v6: DB/트리 값 자체가 바로 상위 Pool을 100으로 보는 상대 배분율이다.
    // 실제 브랜치 Pool은 화면에서도 부모의 실효 Pool × 상대 배분율로 계산해서 보여준다.
    const parentPool = isRoot ? 100 : Math.max(0, Math.min(100, Number(ancestorFeeTotal) || 0));
    const allocationPercent = isRoot
      ? 100
      : Math.max(0, Math.min(100, Number(node.fee_percent) || 0));
    const branchPool = isRoot
      ? 100
      : Math.round((parentPool * allocationPercent) * 100) / 10000;
    const maxFeeForCurrent = 100;
    const remainingForChild = branchPool;
    const segments = React.useMemo(() => ([{
        label: t('현재 브랜치 풀'),
        value: branchPool,
        colorClass: 'bg-gray-900',
    }]), [branchPool]);
    const total = 100;
    const handleCreate = async () => {
        setMenuOpen(false);
        setOpenCreateModal(true);
    };
    const handleInfoEdit = () => {
        setMenuOpen(false);
        setInfoOpen(true);
    };
    const handleBlockAgent = async () => {
        setMenuOpen(false);
        if (isRoot) return;
        const nextBlocked = !Boolean(node.is_banned);
        const actionLabel = nextBlocked ? t("차단") : t("차단 해제");
        if (!window.confirm(`${node.agent_id} ${actionLabel}?`)) return;
        await onBlock?.(node.agent_id, nextBlocked);
    };
    const handleDeleteAgent = async () => {
        setMenuOpen(false);
        if (!canDeleteAgent) return;
        await onDelete?.(node.agent_id, { type: 'agent', label: node.nickname || node.agent_id });
    };
    return (<div
      className={`admin-agent-card ${expanded ? 'is-expanded' : 'is-collapsed'} rounded-xl border p-4 mb-4 relative ${isRoot ? 'border-indigo-400' : 'border-gray-200'}`}
      data-collapse-depth={collapseDepth}
      data-collapsible="true"
    >
      {/* 헤더 */}
      <div className="agent-head flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start md:items-center gap-2">
          <button className="agent-toggle w-7 h-7 rounded-full border flex items-center justify-center shrink-0" title={expanded ? t("\uC811\uAE30") : t("\uD3BC\uCE58\uAE30")} aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
            {expanded ? '▾' : '▸'}
          </button>

          <div className="min-w-0">
            <div className="text-base sm:text-lg font-semibold flex flex-wrap items-center gap-2">
              <span className="truncate">{node.agent_id}</span>
              <span className="text-gray-500 text-xs sm:text-sm">
                [lv{String(node.level)}] {node.is_banned ? 'blocked' : (node.status || 'active')}
              </span>
            </div>

            <div className="text-[11px] sm:text-xs text-gray-500 break-all">
              line: {node.line_code}
            </div>

            <div className="mt-1 text-[11px] sm:text-xs text-gray-600 flex flex-wrap items-center justify-between gap-3">
              <span className="min-w-0">{t('실효 Pool ')}{branchPool}{t('% · 설정 ')}{allocationPercent}{t('% · 하위에이전트')}{counts.agentCnt}{t("\u00B7 \uC18C\uC18D\uC720\uC800")}{' '}
                {counts.userCnt}
                <span className="ml-2 text-amber-700">mission: {String(localMissionRewardPolicy?.mode || 'SELF')}</span>
              </span>

              {!isRoot && (<AgentAssetControl className="shrink-0" targetId={node.user_code ?? node.agent_id} seedAsset={Number(node.asset) || 0} onCredit={onCredit} onDebit={onDebit}/>)}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 relative self-start md:self-auto max-w-full">
          <div className="relative">
            <button ref={menuBtnRef} className="px-3 py-1 rounded-lg text-sm bg-black text-white" onClick={() => setMenuOpen((v) => !v)}>{t("\uC218\uC815")}</button>

            <Menu open={menuOpen} anchorRef={menuBtnRef} onClose={() => setMenuOpen(false)} ref={menuRef}>
              <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={handleCreate}>{t("\uD558\uC704 \uC0DD\uC131")}</button>
              {!isRoot && (<button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={handleBlockAgent}>{node.is_banned ? t("차단 해제") : t("차단")}</button>)}
              <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={handleInfoEdit}>{t("\uC815\uBCF4\uC218\uC815")}</button>
              {canDeleteAgent && (<button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" onClick={handleDeleteAgent}>{t("\uC0AD\uC81C")}</button>)}
            </Menu>
          </div>

          {!isRoot && (<button className="px-3 py-1 rounded-lg text-sm border" onClick={() => setFeeOpen(true)} title={t('브랜치 수수료 수정')}>{t('수수료 수정')}</button>)}

          <button className="px-3 py-1 rounded-lg text-sm border border-amber-500 text-amber-700" onClick={() => setBeneficiaryOpen(true)} title={t('미션 상금 수혜자 설정')}>{t('상금 수혜자')}</button>

          {isLv4 && hasUsers && (<button className="px-3 py-1 rounded-lg text-sm border border-indigo-500 text-indigo-600" onClick={() => setPlayerFeeOpen(true)} title={t("\uC774 \uC5D0\uC774\uC804\uD2B8 \uBC11 \uBAA8\uB4E0 \uD50C\uB808\uC774\uC5B4 \uC218\uC218\uB8CC\uB97C \uD55C \uBC88\uC5D0 \uC124\uC815")}>{t("\uD50C\uB808\uC774\uC5B4 \uC218\uC218\uB8CC \uC124\uC815")}</button>)}
        </div>
      </div>

      {expanded && (<>
          {/* Fee 게이지 */}
          <div className="mt-3">
            <FeeGauge segments={segments} total={total}/>
            <div className="mt-1 text-[11px] sm:text-xs text-gray-500">{t('설정값은 바로 상위에서 내려온 Pool을 100으로 보는 상대비율입니다. 실효 Pool은 상위 Pool × 설정값으로 계산됩니다.')}</div>
          </div>

          {/* 소속 유저 */}
          {!!(node.users && node.users.length) && (<div className="mt-5">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">{t("\uC18C\uC18D \uC720\uC800")}</div>
                <div className="text-xs text-slate-500">{t("\uCD1D")}{node.users.length}{t("\uBA85")}</div>
              </div>

              <UserTableList
                users={node.users}
                onBlock={onBlock}
                onDelete={onDelete}
                onCredit={onCredit}
                onDebit={onDebit}
                canDelete={canDeleteUsersHere}
              />
            </div>)}

          {/* 하위 에이전트들 */}
          {!!(node.children && node.children.length) && (<div className="admin-agent-children mt-4 pl-3 md:pl-4 border-l">
              {node.children.map((child) => (<AgentTreeNode key={child.agent_id} node={child} onCreate={onCreate} onBlock={onBlock} onDelete={onDelete} onUpdateFee={onUpdateFee} onCredit={onCredit} onDebit={onDebit} viewerIsSuperadmin={viewerIsSuperadmin} ancestorFeeTotal={branchPool} depth={Number(depth) + 1}/>))}
            </div>)}
        </>)}

      {/* 수수료 수정 모달 */}
      {feeOpen &&
            (() => {
                const initial = Math.min(100, allocationPercent);
                return (<EditFeeModal maxPercent={maxFeeForCurrent} ancestorPercent={parentPool} initialPercent={initial} onClose={() => setFeeOpen(false)} onSubmit={async (nextPercent) => {
                        const pct = Math.round(Math.min(100, Math.max(0, Number(nextPercent) || 0)) * 10000) / 10000;
                        setFeeOpen(false);
                        await onUpdateFee?.(node.agent_id, pct);
                    }}/>);
            })()}

      {/* lv4 전용 플레이어 수수료 통일 모달 */}
      {playerFeeOpen &&
            (() => {
                const users = Array.isArray(node.users) ? node.users : [];
                const firstUser = users[0];
                const currentAllocation = firstUser ? Number(firstUser.fee_percent) || 0 : 0;
                const maxUserFee = 100;
                const initial = Math.min(100, Math.max(0, currentAllocation));
                return (<EditFeeModal title={t('플레이어 수수료 설정')} maxPercent={maxUserFee} ancestorPercent={branchPool} initialPercent={initial} onClose={() => setPlayerFeeOpen(false)} onSubmit={async (nextPercent) => {
                        const pct = Math.round(Math.min(100, Math.max(0, Number(nextPercent) || 0)) * 10000) / 10000;
                        setPlayerFeeOpen(false);
                        if (firstUser) {
                            await onUpdateFee?.(firstUser.user_code, pct);
                        }
                    }}/>);
            })()}

      {/* 에이전트 정보 수정 */}
      <InfoEditModal open={infoOpen} targetId={node.agent_id} defaultNickname={node.nickname} onClose={() => setInfoOpen(false)} onApply={async (payload) => {
            try {
                await api.post('/admin/users/update', { target_id: node.agent_id, ...payload });
                setInfoOpen(false);
            }
            catch (e) {
                alert(e?.response?.data?.message || e.message || t("\uC815\uBCF4 \uC218\uC815 \uC2E4\uD328"));
            }
        }}/>

      {/* 미션 상금 수혜자 설정 */}
      <MissionBeneficiaryModal open={beneficiaryOpen} agentId={node.agent_id} initialPolicy={localMissionRewardPolicy} onClose={() => setBeneficiaryOpen(false)} onSaved={(policy) => setLocalMissionRewardPolicy(policy || { mode: 'SELF', receivers: [] })}/>

      {/* 하위 생성 폼 */}
      {openCreateModal && (<CreateChildFallback parentAgentId={node.agent_id} onClose={() => setOpenCreateModal(false)} onSubmit={async (payload) => {
                await onCreate({ ...payload, parent_agent_id: node.agent_id });
                setOpenCreateModal(false);
            }}/>)}
    </div>);
}
AgentTreeNode.propTypes = {
    node: PropTypes.shape({
        agent_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        line_code: PropTypes.string,
        level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        fee_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        status: PropTypes.string,
        is_banned: PropTypes.bool,
        nickname: PropTypes.string,
        user_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        asset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        mission_reward_policy: PropTypes.object,
        users: PropTypes.arrayOf(PropTypes.shape({
            user_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            nickname: PropTypes.string,
            is_banned: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
            fee_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            asset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })),
        children: PropTypes.array,
    }).isRequired,
    onCreate: PropTypes.func.isRequired,
    onBlock: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdateFee: PropTypes.func,
    onCredit: PropTypes.func,
    onDebit: PropTypes.func,
    isRoot: PropTypes.bool,
    viewerIsSuperadmin: PropTypes.bool,
    ancestorFeeTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    depth: PropTypes.number,
};
